// Copyright (C) 2021 TANNER AG

import React, { FC, useMemo, useState, useEffect } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import { CombinedError } from "urql";

import { ChevronRightIcon } from "../../components/icons/MANIcons";
import { CategoriesTreeItemQuery, CategoryTreeData } from "../../graphql";
import GqlError from "../../components/error/GqlError";
import Spinner from "../../components/Spinner";

const findPathToNode = (
    items: CategoryTreeData[],
    targetId: string,
    currentPath: string[] = []
): string[] | null => {
    for (const item of items) {
        const newPath = [...currentPath, item.id];
        if (item.id === targetId) return newPath;

        if (item.children?.length) {
            const pathInChildren = findPathToNode(item.children, targetId, newPath);
            if (pathInChildren) return pathInChildren;
        }
    }
    return null;
};

type SelectArgs = {
    path: string[];
    level: number;
    hasChildren: boolean;
};

interface RenderMenuProps {
    items?: CategoryTreeData[];
    level?: number;
    path?: string[]; // ancestry ids to this level
    onSelect: (args: SelectArgs) => void;
    activePath: string[];
    onToggleExpand: (id: string) => void;
    expandedIds: string[];
}

const RenderMenu: React.FC<RenderMenuProps> = ({
                                                   items,
                                                   level = 0,
                                                   path = [],
                                                   onSelect,
                                                   activePath,
                                                   onToggleExpand,
                                                   expandedIds,
                                               }) => {
    return (
        <List dense disablePadding sx={{ p: 1 }}>
            {items?.map(({ id, title, children }) => {
                const nodePath = [...path, id];
                const isActive = activePath.includes(id);
                const hasChildren = !!children?.length;
                const isExpanded = expandedIds.includes(id);

                const RowContent = (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect({ path: nodePath, level, hasChildren });
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            cursor: "pointer",
                        }}
                    >
                        <Box sx={{ width: "50px", flexShrink: 0 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "bold",
                                    color: "error.main",
                                    minWidth: "24px",
                                    textAlign: "left",
                                    fontSize: "16px",
                                }}
                            >
                                {id}
                            </Typography>
                        </Box>

                        <Box sx={{ width: "100%" }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: "text.primary",
                                    fontWeight: "bold",
                                    fontSize: level > 0 ? "13px" : "14px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                }}
                            >
                                {title}
                            </Typography>
                        </Box>
                    </Box>
                );

                if (hasChildren) {
                    return (
                        <Accordion
                            key={id}
                            expanded={isExpanded}
                            disableGutters
                            elevation={0}
                            square
                            sx={{
                                border: level === 0 ? "1px solid #e0e0e0" : "none",
                                "&:before": { display: "none" },
                                mt: level === 0 ? 2 : 0,
                                borderRadius: level === 0 ? 1 : 0,
                                "& >.MuiButtonBase-root": {
                                    backgroundColor: isActive ? "#eaedf3" : "transparent",
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    <ChevronRightIcon
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleExpand(id);
                                        }}
                                        sx={{
                                            transform: isExpanded ? "rotate(90deg)" : "none",
                                            transition: "transform 0.3s ease-in-out",
                                            color: "text.secondary",
                                            padding: "5px",
                                            borderRadius: "50%",
                                            "&:hover": { backgroundColor: "action.hover" },
                                        }}
                                    />
                                }
                                // prevent default accordion toggle behavior on summary click
                                onClick={(e) => e.preventDefault()}
                                sx={{
                                    minHeight: "48px !important",
                                    height: "48px",
                                    backgroundColor: "transparent",
                                    "&.Mui-focusVisible": { backgroundColor: "primary.light" },
                                    "& .MuiAccordionSummary-content": {
                                        margin: "0 !important",
                                        alignItems: "center",
                                        gap: 1,
                                    },
                                    "& .MuiAccordionSummary-expandIconWrapper": {
                                        transform: "none !important",
                                        color: "text.secondary",
                                    },
                                    borderRadius: level > 0 ? 1 : 0,
                                    "&:hover": {
                                        backgroundColor: !isActive ? "#f5f5f5" : undefined,
                                    },
                                }}
                            >
                                {RowContent}
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0, pl: 2 }}>
                                <RenderMenu
                                    items={children}
                                    level={level + 1}
                                    path={nodePath}
                                    onSelect={onSelect}
                                    activePath={activePath}
                                    onToggleExpand={onToggleExpand}
                                    expandedIds={expandedIds}
                                />
                            </AccordionDetails>
                        </Accordion>
                    );
                }

                // Leaf node (could be level 1 category, or level 2 category)
                return (
                    <ListItemButton
                        key={id}
                        onClick={() => onSelect({ path: nodePath, level, hasChildren })}
                        selected={isActive}
                        sx={{
                            minHeight: "48px",
                            height: "fit-content",
                            py: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "#eaedf3",
                                "&:hover": { backgroundColor: "#eaedf3" },
                            },
                            borderRadius: 1,
                            border: level === 0 ? "1px solid #e0e0e0" : "none",
                            mt: level === 0 ? 1 : 0,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: "100%" }}
                        >
                            <Box sx={{ width: "50px", flexShrink: 0 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: "bold",
                                        color: "error.main",
                                        minWidth: "24px",
                                        textAlign: "left",
                                        fontSize: "16px",
                                    }}
                                >
                                    {id}
                                </Typography>
                            </Box>

                            <Box sx={{ width: "100%", ml: 1 }}>
                                <Tooltip title={title}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: "text.primary",
                                            fontWeight: "bold",
                                            fontSize: level > 0 ? "13px" : "14px",
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        </Stack>
                    </ListItemButton>
                );
            })}
        </List>
    );
};

const CodeCatalogHierarchy: FC<{
    hierarchyData?: CategoriesTreeItemQuery;
    hierarchyFetching?: boolean;
    hierarchyError?: CombinedError;
}> = ({ hierarchyData, hierarchyFetching, hierarchyError }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandedIds, setExpandedIds] = useState<string[]>([]);

    const parentCategoryID = searchParams.get("parentCategory");
    const subCategoryID = searchParams.get("subCategory");
    const categoryID = searchParams.get("category");

    const activeTargetId = categoryID || subCategoryID || parentCategoryID || null;

    const activePath = useMemo(() => {
        if (!activeTargetId || !hierarchyData?.categoriesTreeItem?.data) return [];
        return findPathToNode(hierarchyData.categoriesTreeItem.data, activeTargetId) || [];
    }, [activeTargetId, hierarchyData]);

    useEffect(() => {
        // Expand all parents so the active node is visible
        const parentsToExpand = activePath.slice(0, -1);
        setExpandedIds((prev) => Array.from(new Set([...prev, ...parentsToExpand])));
    }, [activePath]);

    const handleToggleExpand = (id: string) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSelect = ({ path, level, hasChildren }: SelectArgs) => {
        const next = new URLSearchParams(searchParams);

        // Clear other filters
        next.delete("series");
        next.delete("types");
        next.delete("page");

        // Reset hierarchy
        next.delete("parentCategory");
        next.delete("subCategory");
        next.delete("category");

        const lvl1 = path[0]; // parentCategory
        const lvl2 = path[1]; // subCategory OR category
        const lvl3 = path[2]; // category (only in 3-level case)

        if (lvl1) next.set("parentCategory", lvl1);

        // 1 level: parentCategory only
        if (path.length === 1) {
            setSearchParams(next);
            return;
        }

        // 2 levels:
        // - if clicked node at level 2 has children => treat it as subCategory
        // - if leaf => treat it as category
        if (path.length === 2) {
            if (level === 1 && hasChildren) {
                next.set("subCategory", lvl2);
            } else {
                next.set("category", lvl2);
            }
            setSearchParams(next);
            return;
        }

        // 3 levels: parentCategory + subCategory + category
        if (path.length >= 3) {
            next.set("subCategory", lvl2);
            next.set("category", lvl3);
            setSearchParams(next);
        }
    };
    const sortTreeSubcategoriesFirst = (items?: CategoryTreeData[]): CategoryTreeData[] => {
        if (!items) return [];

        const sorted = [...items].sort((a, b) => {
            const aHasChildren = (a.children?.length ?? 0) > 0;
            const bHasChildren = (b.children?.length ?? 0) > 0;

            // subCategory (has children) first
            if (aHasChildren === bHasChildren) return 0;
            return aHasChildren ? -1 : 1;
        });

        return sorted.map((item) => ({
            ...item,
            children: item.children ? sortTreeSubcategoriesFirst(item.children) : item.children,
        }));
    };

// Inside `CodeCatalogHierarchy` component, before `return (...)`
    const sortedHierarchyItems = useMemo(
        () => sortTreeSubcategoriesFirst(hierarchyData?.categoriesTreeItem?.data),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hierarchyData]
    );

    if (hierarchyError) return <GqlError error={hierarchyError} />;
    if (hierarchyFetching) return <Spinner />;

    return (
        <Paper elevation={1} sx={{ height: "100%" }}>
            <RenderMenu
                items={sortedHierarchyItems}
                onSelect={handleSelect}
                activePath={activePath}
                onToggleExpand={handleToggleExpand}
                expandedIds={expandedIds}
            />
        </Paper>
    );
};

export default CodeCatalogHierarchy;
